import { useState, useEffect } from "react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { waLink } from "@/lib/phoneUtils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  HandHeart, Search, Download, Plus, Filter, CreditCard, Building2,
  Banknote, Package, CheckCircle, Clock, AlertCircle, X, ChevronRight, FileText, History,
  MessageCircle, Copy, Phone, Mail, User
} from "lucide-react";

const TYPE_LABELS = { genel: "Genel", general: "Genel", mama: "Mama", meal: "Mama", vet: "Veteriner", vet_visit: "Veteriner", monthly_general: "Aylık Düzenli", koruyucu_aile: "Koruyucu Aile", guardian: "Koruyucu Aile", diger: "Diğer", other: "Diğer", ayni: "Ayni" };
const METHOD_LABELS = { fonzip: "Kart (Fonzip)", bank_transfer: "Banka (IBAN)", nakit: "Nakit", ayni: "Ayni", manual: "Manuel" };
const STATUS_LABELS = { completed: "Tamamlandı", pending: "Beklemede", beklemede: "Beklemede", pending_payment: "Ödeme Bekleniyor", pending_fonzip: "Fonzip Ödemesi Bekleniyor", payment_reported: "Ödeme Bildirildi", payment_failed: "Başarısız", refunded: "İade", cancelled: "İptal" };
const METHOD_ICONS = { fonzip: CreditCard, bank_transfer: Building2, nakit: Banknote, ayni: Package, manual: Banknote };

function StatusBadge({ status }) {
  const colors = {
    completed: "bg-green-100 text-green-700",
    pending: "bg-amber-100 text-amber-700",
    beklemede: "bg-amber-100 text-amber-700",
    pending_payment: "bg-amber-100 text-amber-700",
    payment_failed: "bg-red-100 text-red-700",
    refunded: "bg-purple-100 text-purple-700",
  };
  return <Badge className={colors[status] || "bg-muted"}>{STATUS_LABELS[status] || status}</Badge>;
}

export default function DonationsManagement() {
  const [donations, setDonations] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterMethod, setFilterMethod] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedDonation, setSelectedDonation] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [donorHistory, setDonorHistory] = useState(null);
  const [dogs, setDogs] = useState([]);

  // Add form
  const [addForm, setAddForm] = useState({
    donor_name: "", donor_email: "", donor_phone: "", amount: "",
    donation_type: "genel", payment_method: "nakit", dog_id: "", dog_name: "",
    receipt_no: "", collector: "", message: "", is_anonymous: false,
    in_kind_description: "", in_kind_value: "", donation_date: ""
  });

  useEffect(() => { fetchDonations(); fetchDogs(); }, []);

  const fetchDogs = async () => {
    try {
      const res = await axios.get(`${API}/dogs?limit=100`);
      setDogs(res.data.dogs || []);
    } catch {}
  };

  const fetchDonations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType !== "all") params.set("donation_type", filterType);
      if (filterMethod !== "all") params.set("payment_method", filterMethod);
      if (filterStatus !== "all") params.set("status", filterStatus);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (search) params.set("search", search);
      const res = await axios.get(`${API}/donations?${params.toString()}`);
      setDonations(res.data.donations || []);
      setStats({
        total: res.data.total_amount,
        count: res.data.total_count,
        avg: res.data.avg_amount,
        pending_iban: res.data.pending_iban,
        by_method: res.data.breakdown_method,
        by_status: res.data.breakdown_status
      });
    } catch (err) {
      toast.error("Bağışlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (donation) => {
    setSelectedDonation(donation);
    setDonorHistory(null);
    try {
      const res = await axios.get(`${API}/donations/${donation.donation_id}/audit`);
      setAuditLogs(res.data.logs || []);
    } catch { setAuditLogs([]); }
    // Fetch donor history if email exists
    if (donation.donor_email) {
      try {
        const hres = await axios.get(`${API}/donors/${encodeURIComponent(donation.donor_email)}/history`);
        setDonorHistory(hres.data);
      } catch { setDonorHistory(null); }
    }
  };

  const DERNEK_IBAN = "TR36 0001 5001 5800 7319 4625 42";
  const DERNEK_HESAP_SAHIBI = "Pati Bekçileri Derneği";

  const isPaymentPending = (donation) => {
    const st = (donation.status || "").toLowerCase();
    return st === "pending_payment" || st === "pending" || st === "beklemede";
  };

  const generateWhatsAppMessage = (donation) => {
    const name = donation.donor_name || "Değerli Bağışçımız";
    const amount = donation.amount?.toLocaleString("tr-TR");
    const dogName = donation.dog_name;
    const frontendUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/api$/, "");
    const dogLink = donation.dog_id ? `${frontendUrl}/can/${donation.dog_id}` : null;
    const type = donation.donation_type;

    // Ödeme Bekleniyor — bağış niyeti var ama teknik/ödeme aksadı → nazikçe hatırlat + IBAN sun
    if (isPaymentPending(donation)) {
      const referans = donation.reference_code || donation.donation_id || "";
      const typeLabel = (type === "vet" || type === "veteriner") ? "veteriner desteği"
                      : type === "mama" ? "mama desteği"
                      : type === "koruyucu_aile" ? "koruyucu aile desteği"
                      : "bağış";
      return `Sevgili ${name},\n\nBize ulaşan ${amount}₺ tutarındaki ${typeLabel} niyetiniz için içtenlikle teşekkür ederiz 💛\n\nAncak ödemenizin sistemimize henüz ulaşmadığını fark ettik. Kart işleminizde teknik bir aksaklık olmuş olabilir. Merak etmeyin, size başka bir yol sunuyoruz:\n\n🏦 *IBAN ile de destek olabilirsiniz*\nHesap Sahibi: ${DERNEK_HESAP_SAHIBI}\nIBAN: ${DERNEK_IBAN}\nAçıklama: ${referans ? `#${referans}` : `${name} - ${typeLabel}`}\n\n${dogName ? `Bağışınız ${dogName}'ın bakımında kullanılacak.` : "Bağışınız kimsesiz canlarımızın bakımında kullanılacak."}${dogLink && dogName ? `\n\n${dogName}'ın profili:\n${dogLink}` : ""}\n\nHavale/EFT sonrası dekontunuzu bu numaraya iletmeniz yeterli — biz de kaydınızı hemen tamamlayalım.\n\nSevgiyle,\nKAHU Ekibi 🐾`;
    }

    if (type === "vet" || type === "veteriner") {
      return `Sevgili ${name},\n\n${amount}₺ tutarındaki veteriner desteğiniz için çok teşekkür ederiz!\n\nBağışınız sayesinde kimsesiz canlarımız gerekli sağlık hizmetlerine kavuşuyor.${dogName ? ` ${dogName} da sizin desteğinizle daha sağlıklı günlere ulaşacak.` : " Her muayene ve tedavi, bir canın hayata tutunması demek."}${dogLink && dogName ? `\n\n${dogName}'ın profilini buradan takip edebilirsiniz:\n${dogLink}` : ""}\n\nSevgiyle,\nKAHU Ekibi`;
    } else if (type === "mama") {
      return `Sevgili ${name},\n\n${amount}₺ tutarındaki mama desteğiniz için çok teşekkür ederiz!\n\n${dogName ? `${dogName}'ın karnı sizin sayenizde doyuyor!` : "Bağışınız sayesinde kimsesiz canlarımız aç kalmıyor."}${dogLink && dogName ? `\n\n${dogName}'ın profilini buradan takip edebilirsiniz:\n${dogLink}` : ""}\n\nSevgiyle,\nKAHU Ekibi`;
    } else if (type === "koruyucu_aile") {
      return `Sevgili ${name},\n\n${amount}₺ tutarındaki koruyucu aile desteğiniz için çok teşekkür ederiz!\n\n${dogName ? `${dogName}'ın bakımı sizin düzenli desteğinizle güvence altında.` : "Düzenli desteğiniz bir canın yaşam kalitesini doğrudan etkiliyor."}${dogLink && dogName ? `\n\n${dogName}'ın profilini ve güncellemelerini buradan takip edebilirsiniz:\n${dogLink}` : ""}\n\nSevgiyle,\nKAHU Ekibi`;
    } else {
      return `Sevgili ${name},\n\n${amount}₺ tutarındaki bağışınız için çok teşekkür ederiz!\n\n${dogName ? `Bağışınız ${dogName}'ın bakımında kullanılacak.` : "Bağışınız kimsesiz canlarımızın bakımında kullanılacak."} Her destek bir canın hayatında fark yaratıyor.${dogLink && dogName ? `\n\n${dogName}'ın profilini buradan takip edebilirsiniz:\n${dogLink}` : ""}\n\nSevgiyle,\nKAHU Ekibi`;
    }
  };

  const copyWhatsAppMsg = (donation) => {
    const msg = generateWhatsAppMessage(donation);
    navigator.clipboard.writeText(msg)
      .then(() => toast.success("WhatsApp mesajı kopyalandı!"))
      .catch(() => toast.error("Kopyalanamadı — mesajı elle seçin"));
  };

  const openWhatsApp = (donation) => {
    const msg = generateWhatsAppMessage(donation);
    window.open(waLink(donation.donor_phone, msg), "_blank");
  };

  const handleVerify = async (donationId) => {
    try {
      await axios.put(`${API}/donations/${donationId}/verify`, { verified_by: "admin" });
      toast.success("Bağış doğrulandı");
      fetchDonations();
      setSelectedDonation(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Doğrulama başarısız");
    }
  };

  const handleAddDonation = async () => {
    if (!addForm.amount || parseFloat(addForm.amount) <= 0) { toast.error("Tutar gerekli"); return; }
    if (addForm.payment_method === "nakit" && !addForm.receipt_no.trim()) { toast.error("Nakit bağışlarda alındı belgesi no zorunlu"); return; }
    if (addForm.payment_method === "ayni" && !addForm.receipt_no.trim()) { toast.error("Ayni bağışlarda alındı belgesi no zorunlu"); return; }
    try {
      await axios.post(`${API}/donations/manual`, { ...addForm, amount: parseFloat(addForm.amount), in_kind_value: addForm.in_kind_value ? parseFloat(addForm.in_kind_value) : null });
      toast.success("Bağış kaydedildi");
      setShowAddDialog(false);
      setAddForm({ donor_name: "", donor_email: "", donor_phone: "", amount: "", donation_type: "genel", payment_method: "nakit", dog_id: "", dog_name: "", receipt_no: "", collector: "", message: "", is_anonymous: false, in_kind_description: "", in_kind_value: "", donation_date: "" });
      fetchDonations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bağış eklenemedi");
    }
  };

  const exportCSV = () => {
    const headers = ["Bağış ID", "Tarih", "Tutar", "Tür", "Yöntem", "Durum", "Bağışçı", "E-posta", "İlgili Can", "Alındı Belgesi No", "Mesaj"];
    const rows = donations.map(d => [
      d.donation_id, new Date(d.created_at).toLocaleDateString("tr-TR"), d.amount,
      TYPE_LABELS[d.donation_type] || d.donation_type, METHOD_LABELS[d.payment_method] || d.payment_method,
      STATUS_LABELS[d.status] || d.status, d.is_anonymous ? "Anonim" : (d.donor_name || "-"),
      d.donor_email || "-", d.dog_name || "-", d.receipt_no || "-", d.message || "-"
    ]);
    const csv = [headers.join(","), ...rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `kahu_bagislar_${new Date().toISOString().split("T")[0]}.csv`; a.click();
  };

  const exportExcel = async (maskPii = false) => {
    try {
      const params = new URLSearchParams();
      if (filterType !== "all") params.append("donation_type", filterType);
      if (filterMethod !== "all") params.append("payment_method", filterMethod);
      if (filterStatus !== "all") params.append("status", filterStatus);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      if (maskPii) params.append("mask_pii", "true");
      const res = await axios.get(`${API}/donations/export/excel?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kahu-bagislar-${new Date().toISOString().split("T")[0]}${maskPii ? "-maskeli" : ""}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(maskPii ? "KVKK maskeli Excel indirildi" : "Excel dosyası indirildi");
    } catch (e) {
      toast.error("Excel indirilemedi");
    }
  };

  const uploadDekont = async (donationId, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Dosya 10MB sınırını aştı");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/donations/${donationId}/dekont`, fd);
      toast.success("Dekont yüklendi");
      // refresh drawer state
      const updated = await axios.get(`${API}/donations?search=${donationId}`);
      const found = (updated.data.donations || []).find(d => d.donation_id === donationId);
      if (found) setSelectedDonation(found);
      fetchDonations();
      return res.data;
    } catch (e) {
      toast.error("Dekont yüklenemedi: " + (e.response?.data?.detail || e.message));
    }
  };

  const removeDekont = async (donationId) => {
    if (!confirm("Dekontu silmek istediğine emin misin?")) return;
    try {
      await axios.delete(`${API}/donations/${donationId}/dekont`);
      toast.success("Dekont silindi");
      const updated = await axios.get(`${API}/donations?search=${donationId}`);
      const found = (updated.data.donations || []).find(d => d.donation_id === donationId);
      if (found) setSelectedDonation(found);
      fetchDonations();
    } catch (e) {
      toast.error("Dekont silinemedi");
    }
  };

  const applyFilters = () => { fetchDonations(); };
  const clearFilters = () => {
    setFilterType("all"); setFilterMethod("all"); setFilterStatus("all");
    setDateFrom(""); setDateTo(""); setSearch("");
    setTimeout(fetchDonations, 50);
  };

  const getMethodBreakdown = (method) => {
    const item = (stats.by_method || []).find(m => m._id === method);
    return item ? { total: item.total, count: item.count } : { total: 0, count: 0 };
  };

  return (
    <div className="space-y-6" data-testid="donations-management">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl">Bağışlar</h1>
          <p className="text-muted-foreground">{stats.count || 0} bağış kaydı</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={exportCSV} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" /> CSV
          </Button>
          <Button variant="outline" onClick={() => exportExcel(false)} data-testid="export-excel-btn">
            <Download className="w-4 h-4 mr-2" /> Excel
          </Button>
          <Button variant="outline" onClick={() => exportExcel(true)} data-testid="export-excel-masked-btn" title="KVKK maskeli (bağışçı PII gizli)">
            <Download className="w-4 h-4 mr-2" /> Excel (KVKK)
          </Button>
          <Button onClick={() => setShowAddDialog(true)} data-testid="add-donation-btn">
            <Plus className="w-4 h-4 mr-2" /> Bağış Ekle
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border border-border/50">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-1">Toplam Bağış</p>
            <p className="font-heading text-2xl text-accent">{(stats.total || 0).toLocaleString("tr-TR")}₺</p>
          </CardContent>
        </Card>
        <Card className="bg-card border border-border/50">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-1">Bağış Adedi</p>
            <p className="font-heading text-2xl">{stats.count || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border border-border/50">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-1">Ortalama</p>
            <p className="font-heading text-2xl">{(stats.avg || 0).toLocaleString("tr-TR")}₺</p>
          </CardContent>
        </Card>
        <Card
          className="bg-card border border-border/50 cursor-pointer hover:border-amber-400 transition-colors"
          onClick={() => { setFilterMethod("bank_transfer"); setFilterStatus("pending"); setTimeout(fetchDonations, 50); }}
          data-testid="iban-queue-card"
          title="IBAN doğrulama kuyruğunu görmek için tıkla"
        >
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground mb-1">IBAN Doğrulama Bekleyen</p>
            <p className="font-heading text-2xl text-amber-600">{stats.pending_iban || 0}</p>
            <p className="text-[11px] text-muted-foreground mt-1">Kuyruğu görüntüle →</p>
          </CardContent>
        </Card>
      </div>

      {/* Method Breakdown */}
      <div className="grid grid-cols-4 gap-3">
        {["fonzip", "bank_transfer", "nakit", "ayni"].map(method => {
          const Icon = METHOD_ICONS[method];
          const data = getMethodBreakdown(method);
          return (
            <div key={method} className="flex items-center gap-3 p-3 bg-muted/30 rounded-xl">
              <Icon className="w-5 h-5 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">{METHOD_LABELS[method]}</p>
                <p className="font-medium text-sm">{data.total.toLocaleString("tr-TR")}₺ <span className="text-muted-foreground font-normal">({data.count})</span></p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end p-4 bg-muted/20 rounded-xl">
        <div className="flex-1 min-w-[200px]">
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Ara: isim, e-posta, bağış ID..." data-testid="donation-search" />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-36"><SelectValue placeholder="Tür" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Türler</SelectItem>
            <SelectItem value="genel">Genel</SelectItem>
            <SelectItem value="mama">Mama</SelectItem>
            <SelectItem value="vet">Veteriner</SelectItem>
            <SelectItem value="koruyucu_aile">Koruyucu Aile</SelectItem>
            <SelectItem value="ayni">Ayni</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterMethod} onValueChange={setFilterMethod}>
          <SelectTrigger className="w-36"><SelectValue placeholder="Yöntem" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Yöntemler</SelectItem>
            <SelectItem value="fonzip">Kart (Fonzip)</SelectItem>
            <SelectItem value="bank_transfer">IBAN</SelectItem>
            <SelectItem value="nakit">Nakit</SelectItem>
            <SelectItem value="ayni">Ayni</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-36"><SelectValue placeholder="Durum" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Durumlar</SelectItem>
            <SelectItem value="completed">Tamamlandı</SelectItem>
            <SelectItem value="pending">Beklemede</SelectItem>
            <SelectItem value="pending_payment">Ödeme Bekleniyor</SelectItem>
            <SelectItem value="payment_failed">Başarısız</SelectItem>
          </SelectContent>
        </Select>
        <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-36" />
        <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-36" />
        <Button onClick={applyFilters} size="sm"><Filter className="w-4 h-4 mr-1" /> Filtrele</Button>
        <Button onClick={clearFilters} size="sm" variant="ghost">Temizle</Button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><div className="spinner"></div></div>
      ) : donations.length === 0 ? (
        <Card><CardContent className="py-20 text-center">
          <HandHeart className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
          <p className="text-muted-foreground">Bağış bulunamadı</p>
        </CardContent></Card>
      ) : (
        <Card className="bg-card border border-border/50">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/50 bg-muted/30">
                    <th className="text-left p-3 font-medium">ID</th>
                    <th className="text-left p-3 font-medium">Bağışçı</th>
                    <th className="text-left p-3 font-medium">Tutar</th>
                    <th className="text-left p-3 font-medium">Tür</th>
                    <th className="text-left p-3 font-medium">Yöntem</th>
                    <th className="text-left p-3 font-medium">Durum</th>
                    <th className="text-left p-3 font-medium">İlgili Can</th>
                    <th className="text-left p-3 font-medium">Tarih</th>
                    <th className="text-left p-3 font-medium">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {donations.map(d => {
                    const MIcon = METHOD_ICONS[d.payment_method] || Banknote;
                    return (
                      <tr key={d.donation_id} className="border-b border-border/30 hover:bg-muted/20 cursor-pointer" onClick={() => openDetail(d)}>
                        <td className="p-3 font-mono text-xs text-muted-foreground">{d.donation_id.slice(-8)}</td>
                        <td className="p-3">
                          <p className="font-medium text-sm">{d.is_anonymous ? "Anonim" : (d.donor_name || "-")}</p>
                          {!d.is_anonymous && d.donor_email && <p className="text-xs text-muted-foreground">{d.donor_email}</p>}
                        </td>
                        <td className="p-3 font-semibold">{d.amount?.toLocaleString("tr-TR")}₺</td>
                        <td className="p-3"><Badge variant="outline" className="text-xs">{TYPE_LABELS[d.donation_type] || d.donation_type}</Badge></td>
                        <td className="p-3"><span className="flex items-center gap-1 text-xs"><MIcon className="w-3.5 h-3.5" />{METHOD_LABELS[d.payment_method] || d.payment_method}</span></td>
                        <td className="p-3"><StatusBadge status={d.status} /></td>
                        <td className="p-3 text-xs text-muted-foreground">{d.dog_name || "-"}</td>
                        <td className="p-3 text-xs text-muted-foreground">{new Date(d.created_at).toLocaleDateString("tr-TR")}</td>
                        <td className="p-3">
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openDetail(d); }}>
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detail Drawer */}
      <Dialog open={!!selectedDonation} onOpenChange={() => setSelectedDonation(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-xl">Bağış Detayı</DialogTitle></DialogHeader>
          {selectedDonation && (
            <div className="space-y-5 mt-2">
              {/* Basic Info */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Bağış ID</span>
                  <span className="font-mono text-sm">{selectedDonation.donation_id}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Tutar</span>
                  <span className="font-heading text-xl text-accent">{selectedDonation.amount?.toLocaleString("tr-TR")}₺</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Tür</span>
                  <Badge variant="outline">{TYPE_LABELS[selectedDonation.donation_type] || selectedDonation.donation_type}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Yöntem</span>
                  <span className="text-sm">{METHOD_LABELS[selectedDonation.payment_method] || selectedDonation.payment_method}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Durum</span>
                  <StatusBadge status={selectedDonation.status} />
                </div>
                {selectedDonation.dog_name && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">İlgili Can</span>
                    <span className="text-sm font-medium">{selectedDonation.dog_name}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Tarih</span>
                  <span className="text-sm">{new Date(selectedDonation.created_at).toLocaleString("tr-TR")}</span>
                </div>
              </div>

              {/* Donor Info - Enhanced */}
              <div className="p-4 bg-muted/30 rounded-xl space-y-3">
                <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5 mb-3">
                  <User className="w-3.5 h-3.5" /> Bağışçı Bilgileri
                </p>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Ad Soyad</span>
                    <span className="text-sm font-medium">{selectedDonation.is_anonymous ? "Anonim" : (selectedDonation.donor_name || "-")}</span>
                  </div>
                  {selectedDonation.donor_email && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground flex items-center gap-1"><Mail className="w-3 h-3" /> E-posta</span>
                      <span className="text-sm">{selectedDonation.donor_email}</span>
                    </div>
                  )}
                  {selectedDonation.donor_phone && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground flex items-center gap-1"><Phone className="w-3 h-3" /> Telefon</span>
                      <span className="text-sm">{selectedDonation.donor_phone}</span>
                    </div>
                  )}
                  {selectedDonation.donor_iban && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground flex items-center gap-1"><Building2 className="w-3 h-3" /> IBAN</span>
                      <span className="text-sm font-mono">{selectedDonation.donor_iban}</span>
                    </div>
                  )}
                  {selectedDonation.donor_tc && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">TC No</span>
                      <span className="text-sm font-mono">{selectedDonation.donor_tc}</span>
                    </div>
                  )}
                  {selectedDonation.message && (
                    <div className="pt-2 border-t border-border/30">
                      <p className="text-xs text-muted-foreground mb-1">Mesaj</p>
                      <p className="text-sm bg-background p-2 rounded-lg">{selectedDonation.message}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* WhatsApp Message — Teşekkür veya Ödeme Hatırlatma (status'a göre) */}
              {(() => {
                const pending = isPaymentPending(selectedDonation);
                const boxCls = pending
                  ? "p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-3"
                  : "p-4 bg-green-50 border border-green-200 rounded-xl space-y-3";
                const labelCls = pending ? "text-xs font-medium text-amber-700 flex items-center gap-1.5" : "text-xs font-medium text-green-700 flex items-center gap-1.5";
                const innerCls = pending ? "p-3 bg-white rounded-lg border border-amber-100" : "p-3 bg-white rounded-lg border border-green-100";
                const copyCls = pending
                  ? "flex-1 border-amber-300 text-amber-700 hover:bg-amber-100"
                  : "flex-1 border-green-300 text-green-700 hover:bg-green-100";
                const openCls = pending
                  ? "flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                  : "flex-1 bg-green-600 hover:bg-green-700 text-white";
                const label = pending ? "WhatsApp Ödeme Hatırlatma Mesajı" : "WhatsApp Teşekkür Mesajı";
                return (
                  <div className={boxCls} data-testid="whatsapp-section">
                    <p className={labelCls}>
                      <MessageCircle className="w-3.5 h-3.5" /> {label}
                    </p>
                    <div className={innerCls}>
                      <p className="text-xs text-muted-foreground whitespace-pre-line break-words leading-relaxed">
                        {generateWhatsAppMessage(selectedDonation)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className={copyCls}
                        onClick={() => copyWhatsAppMsg(selectedDonation)}
                        data-testid="copy-whatsapp-msg"
                      >
                        <Copy className="w-3.5 h-3.5 mr-1.5" /> Mesajı Kopyala
                      </Button>
                      <Button
                        size="sm"
                        className={openCls}
                        onClick={() => openWhatsApp(selectedDonation)}
                        data-testid="open-whatsapp"
                      >
                        <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> WhatsApp'ta Aç
                      </Button>
                    </div>
                  </div>
                );
              })()}

              {/* Document Info */}
              <div className="p-4 bg-muted/30 rounded-xl space-y-2">
                <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" /> Belge Bilgileri
                </p>
                {selectedDonation.receipt_no && <p className="text-sm"><strong>Alındı Belgesi No:</strong> {selectedDonation.receipt_no}</p>}
                {selectedDonation.reference_code && <p className="text-sm"><strong>Açıklama Kodu:</strong> {selectedDonation.reference_code}</p>}
                {selectedDonation.verified_by && <p className="text-sm"><strong>Doğrulayan:</strong> {selectedDonation.verified_by} - {selectedDonation.verified_at && new Date(selectedDonation.verified_at).toLocaleString("tr-TR")}</p>}
                {selectedDonation.collector && <p className="text-sm"><strong>Tahsil Eden:</strong> {selectedDonation.collector}</p>}
                {selectedDonation.in_kind_description && <p className="text-sm"><strong>Ayni Açıklama:</strong> {selectedDonation.in_kind_description}</p>}
                {!selectedDonation.receipt_no && !selectedDonation.reference_code && !selectedDonation.collector && (
                  <p className="text-xs text-muted-foreground">Belge bilgisi girilmemiş</p>
                )}
              </div>

              {/* Dekont / Makbuz */}
              <div className="p-4 bg-muted/30 rounded-xl space-y-3" data-testid="dekont-section">
                <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" /> Dekont / Makbuz
                </p>
                {selectedDonation.dekont_url ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}${selectedDonation.dekont_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary underline break-all"
                      data-testid="dekont-view-link"
                    >
                      {selectedDonation.dekont_filename || "Dekontu Görüntüle"}
                    </a>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => removeDekont(selectedDonation.donation_id)}
                      data-testid="dekont-remove-btn"
                    >
                      <X className="w-3.5 h-3.5 mr-1" /> Sil
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">Banka dekontu veya fotoğraf yükleyin (PDF/JPG/PNG, max 10MB)</p>
                    <Input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.webp,.heic"
                      onChange={(e) => uploadDekont(selectedDonation.donation_id, e.target.files?.[0])}
                      data-testid="dekont-upload-input"
                    />
                  </div>
                )}
                {selectedDonation.dekont_uploaded_at && (
                  <p className="text-[11px] text-muted-foreground">
                    Yüklenme: {new Date(selectedDonation.dekont_uploaded_at).toLocaleString("tr-TR")}
                  </p>
                )}
              </div>

              {/* Actions */}
              {selectedDonation.status !== "completed" && selectedDonation.payment_method === "bank_transfer" && (
                <Button onClick={() => handleVerify(selectedDonation.donation_id)} className="w-full" data-testid="verify-donation-btn">
                  <CheckCircle className="w-4 h-4 mr-2" /> Bağışı Doğrula
                </Button>
              )}

              {/* Donor History */}
              {donorHistory && donorHistory.count > 1 && (
                <div className="p-4 bg-muted/30 rounded-xl space-y-3" data-testid="donor-history">
                  <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                    <HandHeart className="w-3.5 h-3.5" /> Bağışçı Geçmişi ({donorHistory.count} bağış · {donorHistory.total_amount.toLocaleString("tr-TR")}₺)
                  </p>
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {donorHistory.donations.map(d => (
                      <div
                        key={d.donation_id}
                        className={`flex items-center justify-between text-xs p-2 rounded-lg ${d.donation_id === selectedDonation.donation_id ? "bg-primary/10 border border-primary/20" : "bg-background"}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{d.amount?.toLocaleString("tr-TR")}₺</span>
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">{TYPE_LABELS[d.donation_type] || d.donation_type}</Badge>
                          {d.dog_name && <span className="text-muted-foreground">{d.dog_name}</span>}
                        </div>
                        <span className="text-muted-foreground">{new Date(d.created_at).toLocaleDateString("tr-TR")}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Audit Log */}
              {auditLogs.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground flex items-center gap-1"><History className="w-3.5 h-3.5" /> Denetim İzi</p>
                  {auditLogs.map(log => (
                    <div key={log.log_id} className="flex items-start gap-2 text-xs p-2 bg-muted/20 rounded-lg">
                      <span className="text-muted-foreground shrink-0">{new Date(log.created_at).toLocaleString("tr-TR")}</span>
                      <span className="font-medium">{log.action}</span>
                      <span className="text-muted-foreground">{log.details}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Manual Donation Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-xl">Bağış Ekle (Manuel)</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Ödeme Yöntemi</Label>
                <Select value={addForm.payment_method} onValueChange={v => setAddForm(p => ({ ...p, payment_method: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nakit">Nakit</SelectItem>
                    <SelectItem value="ayni">Ayni (Mal/Hizmet)</SelectItem>
                    <SelectItem value="bank_transfer">Banka (IBAN)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Bağış Türü</Label>
                <Select value={addForm.donation_type} onValueChange={v => setAddForm(p => ({ ...p, donation_type: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="genel">Genel</SelectItem>
                    <SelectItem value="mama">Mama</SelectItem>
                    <SelectItem value="vet">Veteriner</SelectItem>
                    <SelectItem value="koruyucu_aile">Koruyucu Aile</SelectItem>
                    <SelectItem value="diger">Diğer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tutar (₺) *</Label>
                <Input type="number" value={addForm.amount} onChange={e => setAddForm(p => ({ ...p, amount: e.target.value }))} className="mt-1" placeholder="0" />
              </div>
              <div>
                <Label>Bağış Tarihi</Label>
                <Input type="date" value={addForm.donation_date} onChange={e => setAddForm(p => ({ ...p, donation_date: e.target.value }))} className="mt-1" />
              </div>
            </div>

            <div>
              <Label>Bağışçı Adı {addForm.is_anonymous && "(Anonim)"}</Label>
              <div className="flex gap-2 mt-1">
                <Input value={addForm.donor_name} onChange={e => setAddForm(p => ({ ...p, donor_name: e.target.value }))} placeholder="Ad Soyad" className="flex-1" />
                <Button type="button" variant={addForm.is_anonymous ? "default" : "outline"} size="sm" onClick={() => setAddForm(p => ({ ...p, is_anonymous: !p.is_anonymous }))}>
                  Anonim
                </Button>
              </div>
            </div>

            <div>
              <Label>Alındı Belgesi Seri/No {(addForm.payment_method === "nakit" || addForm.payment_method === "ayni") ? "*" : ""}</Label>
              <Input value={addForm.receipt_no} onChange={e => setAddForm(p => ({ ...p, receipt_no: e.target.value }))} className="mt-1" placeholder="Örn: A-001234" />
            </div>

            {addForm.payment_method === "ayni" && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Ayni Bağış Açıklaması</Label>
                  <Input value={addForm.in_kind_description} onChange={e => setAddForm(p => ({ ...p, in_kind_description: e.target.value }))} className="mt-1" placeholder="Örn: 15 kg mama" />
                </div>
                <div>
                  <Label>Tahmini Değer (₺)</Label>
                  <Input type="number" value={addForm.in_kind_value} onChange={e => setAddForm(p => ({ ...p, in_kind_value: e.target.value }))} className="mt-1" />
                </div>
              </div>
            )}

            <div>
              <Label>İlgili Can (opsiyonel)</Label>
              <Select value={addForm.dog_id || "none"} onValueChange={v => {
                const dog = dogs.find(d => d.dog_id === v);
                setAddForm(p => ({ ...p, dog_id: v === "none" ? "" : v, dog_name: dog?.name || "" }));
              }}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Belirtilmemiş</SelectItem>
                  {dogs.map(d => <SelectItem key={d.dog_id} value={d.dog_id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Tahsil Eden (opsiyonel)</Label>
              <Input value={addForm.collector} onChange={e => setAddForm(p => ({ ...p, collector: e.target.value }))} className="mt-1" placeholder="Tahsil eden kişi" />
            </div>

            <div>
              <Label>Not</Label>
              <Textarea value={addForm.message} onChange={e => setAddForm(p => ({ ...p, message: e.target.value }))} className="mt-1" rows={2} placeholder="Bağışla ilgili not..." />
            </div>

            <Button onClick={handleAddDonation} className="w-full" data-testid="save-manual-donation">
              <Plus className="w-4 h-4 mr-2" /> Bağış Kaydet
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
